import { useState, useEffect, useCallback, useRef } from "react";
import AsyncSelect from "react-select/async";
import { PiPlus, PiTrash } from "react-icons/pi";
import { getApi, postApi } from "../../../services/api/globalApi";
import { successMessage } from "../../../utils/toastify-message/ToastifyMessage";
import {
  extractMobilesFromWorkbook,
  downloadMobileTemplate,
} from "../../../utils/extractMobilesFromWorkbook";
import Button from "../../../components/common/button/Button";
import DateInput from "../../../components/common/inputs/DateInput";
import ProductsSelect from "../../../components/common/inputs/ProductsSelect";

const EMPTY_CUSTOM_TIER = {
  isItemCountBased: false,
  minPrice: 0,
  maxPrice: null,
  minItemCount: null,
  maxItemCount: null,
  discountPercent: 0,
  isFreeShipping: false,
  hasGift: false,
  giftType: "fixed",
  giftProductSkus: [],
  additionalFixedGiftSkus: [],
  minPriceForGift: null,
};

const EMPTY_EXCEPTION = {
  refUserIds: [],
  refUserNames: [],
  startDateTime: null,
  endDateTime: null,
  type: "exclude",
  customTiers: [],
  includeReferredCustomers: false,
  weeklyOrderLimit: null,
};

const ROLE_LABELS = {
  affiliate: "نماینده",
  seller: "مشاور",
  sub_affiliate: "زیرنماینده",
};

const TYPE_OPTIONS = [
  {
    value: "exclude",
    label: "کمپین اجرا نشود",
    activeClass: "bg-red-50 border-red-300 text-red-700",
  },
  {
    value: "custom",
    label: "شرایط متفاوت",
    activeClass: "bg-blue-50 border-blue-300 text-blue-700",
  },
  {
    value: "include",
    label: "شامل شود",
    activeClass: "bg-violet-50 border-violet-300 text-violet-700",
  },
];

function CustomTierEditor({ tier, onChange, onRemove, index }) {
  const handleField = (field, value) => {
    let parsed = value;
    if (["minPrice", "maxPrice", "discountPercent", "minPriceForGift"].includes(field)) {
      parsed = value === "" || value === null ? null : Number(value);
    } else if (["minItemCount", "maxItemCount"].includes(field)) {
      parsed = value === "" || value === null ? null : parseInt(value, 10);
    }
    onChange({ ...tier, [field]: parsed });
  };

  return (
    <div className="border border-slate-200 rounded-lg p-3 mb-2 bg-white">
      <div className="flex justify-between items-center mb-3">
        <span className="text-xs font-semibold text-slate-500">شرط {index + 1}</span>
        <button
          onClick={onRemove}
          className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
        >
          <PiTrash size={14} />
        </button>
      </div>

      {/* نوع شرط */}
      <div className="flex gap-2 mb-3">
        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-xs font-medium transition-colors ${!tier.isItemCountBased ? "bg-blue-50 border-blue-300 text-blue-700" : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"}`}>
          <input type="radio" checked={!tier.isItemCountBased} onChange={() => handleField("isItemCountBased", false)} className="hidden" />
          قیمت سبد خرید
        </label>
        <label className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-xs font-medium transition-colors ${tier.isItemCountBased ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "bg-white border-gray-200 text-gray-500 hover:bg-gray-50"}`}>
          <input type="radio" checked={!!tier.isItemCountBased} onChange={() => handleField("isItemCountBased", true)} className="hidden" />
          تعداد قلم سبد خرید
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
        {tier.isItemCountBased ? (
          <>
            <div>
              <label className="block text-xs text-gray-500 mb-1">حداقل قلم</label>
              <input
                type="number"
                value={tier.minItemCount ?? ""}
                onChange={(e) => handleField("minItemCount", e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-emerald-400"
                min="1"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">حداکثر قلم (خالی = بدون حد)</label>
              <input
                type="number"
                value={tier.maxItemCount ?? ""}
                onChange={(e) => handleField("maxItemCount", e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-emerald-400"
                min="1"
              />
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="block text-xs text-gray-500 mb-1">حداقل قیمت (تومان)</label>
              <input
                type="number"
                value={tier.minPrice ?? ""}
                onChange={(e) => handleField("minPrice", e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                min="0"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">حداکثر قیمت (خالی = بدون حد)</label>
              <input
                type="number"
                value={tier.maxPrice ?? ""}
                onChange={(e) => handleField("maxPrice", e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                min="0"
              />
            </div>
          </>
        )}
        <div>
          <label className="block text-xs text-gray-500 mb-1">درصد تخفیف</label>
          <input
            type="number"
            value={tier.discountPercent ?? ""}
            onChange={(e) => handleField("discountPercent", e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400"
            min="0"
            max="100"
          />
        </div>
        <div className="flex items-center gap-2 pt-1">
          <input
            type="checkbox"
            checked={tier.isFreeShipping || false}
            onChange={(e) => handleField("isFreeShipping", e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <label className="text-xs text-gray-600">ارسال رایگان</label>
        </div>
        <div className="flex items-center gap-2 pt-1">
          <input
            type="checkbox"
            checked={tier.hasGift || false}
            onChange={(e) => handleField("hasGift", e.target.checked)}
            className="w-4 h-4 rounded"
          />
          <label className="text-xs text-gray-600">دارای هدیه</label>
        </div>
      </div>

      {tier.hasGift && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">نوع هدیه</label>
            <select
              value={tier.giftType || "fixed"}
              onChange={(e) => handleField("giftType", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
            >
              <option value="fixed">ثابت</option>
              <option value="choice">انتخابی</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">حداقل قیمت برای هدیه</label>
            <input
              type="number"
              value={tier.minPriceForGift ?? ""}
              onChange={(e) => handleField("minPriceForGift", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
              min="0"
            />
          </div>
          <div>
            <ProductsSelect
              label="محصولات هدیه"
              oldProducts={tier.giftProductSkus || []}
              onChange={(skus) => handleField("giftProductSkus", skus)}
            />
          </div>
          <div>
            <ProductsSelect
              label="هدیه ثابت اضافی"
              oldProducts={tier.additionalFixedGiftSkus || []}
              onChange={(skus) => handleField("additionalFixedGiftSkus", skus)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ExceptionCard({ exception, index, usage, onChange, onRemove }) {
  const loadUsers = useCallback(async (inputValue) => {
    try {
      const responses = await Promise.all(
        ["affiliate", "seller", "sub_affiliate"].map((role) =>
          getApi({
            path: "admin/users",
            params: { limit: 20, searchText: inputValue || "", role },
          })
        )
      );
      const users = responses.flatMap((r) => r?.data?.result?.data || []);
      return users.map((u) => ({
        value: u._id,
        label: u.name
          ? `${u.mobile || u.phoneNumber} - ${u.name} (${ROLE_LABELS[u.role] || u.role})`
          : u.mobile || u.phoneNumber,
      }));
    } catch {
      return [];
    }
  }, []);

  const selectedUsers = (exception.refUserIds || []).map((id, i) => ({
    value: id,
    label: (exception.refUserNames || [])[i] || id,
  }));

  const handleUserChange = (options) => {
    onChange({
      ...exception,
      refUserIds: (options || []).map((o) => o.value),
      refUserNames: (options || []).map((o) => o.label),
    });
  };

  const fileInputRef = useRef(null);
  const [excelBusy, setExcelBusy] = useState(false);
  const [excelReport, setExcelReport] = useState(null);

  const handleExcelFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setExcelBusy(true);
    setExcelReport(null);
    try {
      const { mobiles, skipped } = extractMobilesFromWorkbook(await file.arrayBuffer());
      if (mobiles.length === 0) {
        setExcelReport({ error: "هیچ شماره موبایل معتبری در فایل پیدا نشد." });
        return;
      }
      const res = await postApi({
        path: "admin/publicFields/campaign-users",
        body: { mobiles },
      });
      const data = res?.data?.result;
      if (!data) {
        setExcelReport({ error: "پاسخ سرور نامعتبر بود؛ دوباره تلاش کنید." });
        return;
      }
      const taken = new Set(exception.refUserIds || []);
      const fresh = (data.users || []).filter((u) => !taken.has(u._id));
      onChange({
        ...exception,
        refUserIds: [...(exception.refUserIds || []), ...fresh.map((u) => u._id)],
        refUserNames: [
          ...(exception.refUserNames || []),
          ...fresh.map(
            (u) => `${u.mobile} - ${u.name} (${ROLE_LABELS[u.role] || u.role})`,
          ),
        ],
      });
      setExcelReport({
        total: mobiles.length,
        added: fresh.length,
        alreadyInList: (data.users || []).length - fresh.length,
        notFound: data.notFound || [],
        ineligible: data.ineligible || [],
        invalid: data.invalid || [],
        skipped,
      });
    } catch (error) {
      console.error(error);
      setExcelReport({ error: "خواندن یا بررسی فایل با خطا مواجه شد." });
    } finally {
      setExcelBusy(false);
    }
  };

  const handleAddCustomTier = () => {
    onChange({
      ...exception,
      customTiers: [...(exception.customTiers || []), { ...EMPTY_CUSTOM_TIER, _tempId: Date.now() }],
    });
  };

  const handleCustomTierChange = (tierIndex, updatedTier) => {
    const newTiers = [...(exception.customTiers || [])];
    newTiers[tierIndex] = updatedTier;
    onChange({ ...exception, customTiers: newTiers });
  };

  const handleRemoveCustomTier = (tierIndex) => {
    onChange({
      ...exception,
      customTiers: (exception.customTiers || []).filter((_, i) => i !== tierIndex),
    });
  };

  const activeType = TYPE_OPTIONS.find((o) => o.value === exception.type);

  return (
    <div className="rounded-xl border border-gray-200 bg-white shadow-sm mb-3 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 bg-gradient-to-l from-slate-50 to-white border-b border-gray-100">
        <div className="flex items-center gap-2">
          <span className="w-5 h-5 rounded-full bg-slate-700 text-white text-xs font-bold flex items-center justify-center">
            {index + 1}
          </span>
          <span className="font-semibold text-sm text-slate-700">استثنا {index + 1}</span>
          {activeType && (
            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${activeType.activeClass}`}>
              {activeType.label}
            </span>
          )}
        </div>
        <button
          onClick={onRemove}
          className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
        >
          <PiTrash size={16} />
        </button>
      </div>

      <div className="p-4 space-y-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1.5">
            انتخاب نماینده، زیرنماینده یا مشاور <span className="text-red-500">*</span>
          </label>
          <AsyncSelect
            cacheOptions={false}
            defaultOptions
            loadOptions={loadUsers}
            value={selectedUsers}
            onChange={handleUserChange}
            placeholder="جستجو بر اساس موبایل یا نام..."
            noOptionsMessage={() => "کاربری یافت نشد"}
            loadingMessage={() => "در حال جستجو..."}
            isMulti
            isClearable
          />

          <div className="flex flex-wrap items-center gap-2 mt-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              className="hidden"
              onChange={handleExcelFile}
            />
            <button
              type="button"
              disabled={excelBusy}
              onClick={() => fileInputRef.current?.click()}
              className="text-xs font-medium text-emerald-700 bg-white border border-emerald-200 rounded-lg px-3 py-1.5 hover:bg-emerald-50 transition-colors disabled:opacity-50"
            >
              {excelBusy ? "در حال بررسی فایل..." : "افزودن از فایل اکسل (بر اساس شماره موبایل)"}
            </button>
            <button
              type="button"
              onClick={downloadMobileTemplate}
              className="text-xs text-gray-500 underline hover:text-gray-700"
            >
              دانلود نمونه فایل
            </button>
          </div>

          {excelReport && (
            <div
              className={`mt-2 rounded-lg border p-3 text-xs space-y-1 ${
                excelReport.error
                  ? "bg-red-50 border-red-200 text-red-700"
                  : "bg-emerald-50 border-emerald-200 text-emerald-800"
              }`}
            >
              {excelReport.error ? (
                <p>{excelReport.error}</p>
              ) : (
                <>
                  <p className="font-semibold">
                    {excelReport.total.toLocaleString("fa-IR")} شماره در فایل ·{" "}
                    {excelReport.added.toLocaleString("fa-IR")} نفر به لیست اضافه شد
                    {excelReport.alreadyInList > 0 &&
                      ` · ${excelReport.alreadyInList.toLocaleString("fa-IR")} نفر از قبل در لیست بود`}
                  </p>
                  {excelReport.notFound.length > 0 && (
                    <p className="text-amber-700">
                      کاربری با این شماره‌ها پیدا نشد ({excelReport.notFound.length.toLocaleString("fa-IR")}):{" "}
                      <span dir="ltr">{excelReport.notFound.join("، ")}</span>
                    </p>
                  )}
                  {excelReport.ineligible.length > 0 && (
                    <p className="text-amber-700">
                      نماینده/مشاور/زیرنماینده نیستند و اضافه نشدند ({excelReport.ineligible.length.toLocaleString("fa-IR")}):{" "}
                      {excelReport.ineligible
                        .map((u) => `${u.mobile}${u.name ? ` (${u.name})` : ""}`)
                        .join("، ")}
                    </p>
                  )}
                  {(excelReport.invalid.length > 0 || excelReport.skipped > 0) && (
                    <p className="text-amber-700">
                      {(excelReport.invalid.length + excelReport.skipped).toLocaleString("fa-IR")} سلول در ستون موبایل شماره معتبر نبود و نادیده گرفته شد.
                    </p>
                  )}
                  <p className="text-gray-500">
                    برای ثبت نهایی، دکمهٔ «ذخیره استثناها» را بزنید.
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <DateInput
            label="از تاریخ (اختیاری)"
            value={exception.startDateTime}
            onChange={(v) => onChange({ ...exception, startDateTime: v })}
          />
          <DateInput
            label="تا تاریخ (اختیاری)"
            value={exception.endDateTime}
            onChange={(v) => onChange({ ...exception, endDateTime: v })}
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-500 mb-1.5">نوع استثنا</label>
          <div className="flex gap-2">
            {TYPE_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-center justify-center px-3 py-1.5 rounded-lg border cursor-pointer text-xs font-medium transition-colors whitespace-nowrap ${
                  exception.type === opt.value
                    ? opt.activeClass
                    : "bg-white border-gray-200 text-gray-400 hover:bg-gray-50 hover:text-gray-600"
                }`}
              >
                <input
                  type="radio"
                  value={opt.value}
                  checked={exception.type === opt.value}
                  onChange={() =>
                    onChange({
                      ...exception,
                      type: opt.value,
                      ...(opt.value !== "custom" ? { customTiers: [] } : {}),
                    })
                  }
                  className="hidden"
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={exception.includeReferredCustomers !== false}
            onChange={(e) => onChange({ ...exception, includeReferredCustomers: e.target.checked })}
            className="w-4 h-4 mt-0.5 rounded"
          />
          <span className="text-xs text-gray-600">
            مشتریان و زیرنماینده‌های معرفی‌شده توسط این کاربران هم شامل شوند
            <span className="block text-gray-400 mt-0.5">
              اگر تیک نخورد، فقط خود کاربران انتخاب‌شده شامل می‌شوند.
            </span>
          </span>
        </label>

        {exception.type !== "exclude" && (
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1.5">
              سقف سفارش‌های کمپین در هفته (خالی = نامحدود)
            </label>
            <input
              type="number"
              min="0"
              value={exception.weeklyOrderLimit ?? ""}
              onChange={(e) =>
                onChange({
                  ...exception,
                  weeklyOrderLimit:
                    e.target.value === "" ? null : Math.max(0, parseInt(e.target.value, 10) || 0),
                })
              }
              className="w-full md:w-60 border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-blue-400"
              placeholder="مثلاً ۲۰۰"
            />
            <span className="block text-xs text-gray-400 mt-1">
              مجموع سفارش‌های پرداخت‌شدهٔ همهٔ افراد این استثنا که تخفیف کمپین گرفته‌اند؛ هر هفته از شنبه ساعت ۰۰:۰۰ صفر می‌شود.
              با پر شدن سقف، کمپین برای این افراد تا شنبهٔ بعد نمایش داده نمی‌شود و اعمال هم نمی‌شود.
            </span>
            {usage && (
              <span
                className={`block text-xs font-semibold mt-1.5 ${
                  usage.used >= usage.limit ? "text-red-600" : "text-emerald-700"
                }`}
              >
                این هفته: {usage.used.toLocaleString("fa-IR")} از {usage.limit.toLocaleString("fa-IR")} سفارش مصرف شده
                {usage.used >= usage.limit ? " — سهمیه تمام شده" : ""}
              </span>
            )}
          </div>
        )}

        {exception.type === "custom" && (
          <div className="border border-blue-100 rounded-xl p-3 bg-blue-50/40">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-blue-700">شرایط اختصاصی کمپین</span>
              <button
                type="button"
                onClick={handleAddCustomTier}
                className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 bg-white border border-blue-200 rounded-lg px-2.5 py-1.5 transition-colors hover:bg-blue-50"
              >
                <PiPlus size={13} />
                افزودن شرط
              </button>
            </div>
            {(exception.customTiers || []).length === 0 && (
              <p className="text-xs text-gray-400 text-center py-3">
                هیچ شرطی تعریف نشده — یک شرط اضافه کنید
              </p>
            )}
            {(exception.customTiers || []).map((tier, tIdx) => (
              <CustomTierEditor
                key={tier._tempId || tier._id || tIdx}
                tier={tier}
                index={tIdx}
                onChange={(updated) => handleCustomTierChange(tIdx, updated)}
                onRemove={() => handleRemoveCustomTier(tIdx)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function CampaignRefExceptions({ initialExceptions = [], onSaved }) {
  const [exceptions, setExceptions] = useState([]);
  const [usageById, setUsageById] = useState({});

  const loadUsage = useCallback(async () => {
    try {
      const res = await getApi({ path: "admin/publicFields/campaign-quota-usage" });
      const items = res?.data?.result?.items || [];
      setUsageById(Object.fromEntries(items.map((item) => [item.id, item])));
    } catch {
      setUsageById({});
    }
  }, []);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  useEffect(() => {
    setExceptions(
      (initialExceptions || []).map((ex, i) => ({
        ...ex,
        refUserIds: ex.refUserIds || [],
        refUserNames: ex.refUserNames || [],
        _tempId: ex._id || `loaded-${i}-${Date.now()}`,
        customTiers: (ex.customTiers || []).map((t, j) => ({
          ...t,
          _tempId: t._id || `tier-${j}`,
        })),
      }))
    );
  }, [initialExceptions]);

  const handleAdd = () => {
    setExceptions([...exceptions, { ...EMPTY_EXCEPTION, _tempId: Date.now() }]);
  };

  const handleChange = (index, updated) => {
    const next = [...exceptions];
    next[index] = updated;
    setExceptions(next);
  };

  const handleRemove = (index) => {
    setExceptions(exceptions.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    const toSave = exceptions.map(({ _tempId, ...ex }) => ({
      ...ex,
      customTiers: (ex.customTiers || []).map(({ _tempId: _t, ...t }) => t),
    }));
    try {
      await postApi({
        path: "admin/publicFields",
        body: { campaignRefExceptions: toSave },
      });
      successMessage("استثناهای کمپین با موفقیت ذخیره شد");
      if (onSaved) onSaved();
      loadUsage();
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <Button text="افزودن استثنا" color="primary" onClick={handleAdd} icon={<PiPlus />} />
      </div>

      {exceptions.length === 0 && (
        <p className="text-gray-400 text-center py-6 text-sm">هیچ استثنایی تعریف نشده است</p>
      )}

      {exceptions.map((ex, index) => (
        <ExceptionCard
          key={ex._tempId || ex._id || index}
          exception={ex}
          index={index}
          usage={ex._id ? usageById[ex._id] : undefined}
          onChange={(updated) => handleChange(index, updated)}
          onRemove={() => handleRemove(index)}
        />
      ))}

      {exceptions.length > 0 && (
        <div className="mt-4">
          <Button text="ذخیره استثناها" color="success" onClick={handleSave} />
        </div>
      )}
    </div>
  );
}
